// Login.js - Using brand colors #005b82, #2586bc, and #e83d33 with modern icons
import React, { useState } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  useTheme,
  alpha,
  Fade,
  Slide,
  InputAdornment,
  IconButton,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  CorporateFare,
  Radar,
  ReceiptLong,
  TrendingUp,
  Analytics,
  SatelliteAlt,
  Inventory2,
  AccountTree,
  QueryStats,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  // Brand colors from your logo
  const brandColors = {
    primary: "#005b82", // Dark blue
    secondary: "#2586bc", // Medium blue
    accent: "#e83d33", // Red accent
  };

  // Modern icons for feature points
  const featureIcons = [
    { text: "Real-time sales tracking", icon: SatelliteAlt },
    { text: "Supplier purchase orders tracking", icon: Inventory2 },
    { text: "Sales pipeline management", icon: AccountTree },
    { text: "Advanced analytics", icon: QueryStats },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setError("Please enter both username and password");
      return;
    }
    setLoading(true);
    setError("");
    const result = await login(credentials);
    if (result.success) navigate("/dashboard");
    else setError(result.error || "Login failed. Please try again.");
    setLoading(false);
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `
          url('/static/image/login.jpeg') center/cover
        `,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Grid
        container
        sx={{
          maxWidth: 1200,
          minHeight: 700,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          backgroundColor: "white",
        }}
      >
        {/* LEFT SIDE - Branding with Background Image */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: { xs: 4, md: 6, lg: 8 },
            background: `
              linear-gradient(135deg, ${alpha(
                brandColors.primary,
                0.95
              )} 0%, ${alpha(brandColors.secondary, 0.95)} 100%),
              url('/static/image/login.jpeg') center/cover
            `,
            color: "white",
            position: "relative",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Slide in direction="right" timeout={500}>
              <Box>
                {/* Logo */}
                  <Box
            sx={{
              mb: 4,
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src="/static/image/opslogos.png"
              alt="Maritronix Logo"
              sx={{
                height: { xs: 100, md: 100 },
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
              }}
              onError={(e) => {
                console.error("Logo failed to load:", e);
                e.target.style.display = "none";
              }}
            />
          </Box>

                {/* Main Content */}
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.2,
                    fontSize: { xs: "2.5rem", md: "3rem", lg: "3.5rem" },
                    mb: 3,
                    textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                    color: "#ffffff",
                  }}
                >
                  Maritime Operations
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      color: "#e0f7ff",
                      fontSize: { xs: "2rem", md: "2.5rem", lg: "3rem" },
                      textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                    }}
                  >
                    Made Simple
                  </Box>
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    opacity: 0.95,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    fontSize: "1.1rem",
                    maxWidth: 400,
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    mb: 4,
                    color: "#f0faff",
                  }}
                >
                  Streamline vessel tracking, purchase orders, and sales
                  pipelines with our all-in-one corporate platform.
                </Typography>

                {/* Feature Points with modern icons */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  {featureIcons.map((feature, index) => {
                    const IconComponent = feature.icon;
                    return (
                      <Box
                        key={feature.text}
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            backgroundColor: alpha(brandColors.accent, 0.9),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backdropFilter: "blur(10px)",
                            border: `1px solid ${alpha("#ffffff", 0.3)}`,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                            },
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: 20,
                              color: "#ffffff",
                              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            opacity: 0.95,
                            fontWeight: 500,
                            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                            color: "#ffffff",
                          }}
                        >
                          {feature.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Slide>
          </Box>
        </Grid>

        {/* RIGHT SIDE - Login Form with Logo */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 4, md: 6, lg: 8 },
            backgroundColor: "background.paper",
            position: "relative",
            background: `
              linear-gradient(135deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha(
              "#f8fafc",
              0.98
            )} 100%)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Company Logo */}
          <Box
            sx={{
              mb: 4,
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src="/static/image/logo.png"
              alt="Maritronix Logo"
              sx={{
                height: { xs: 60, md: 80 },
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
              }}
              onError={(e) => {
                console.error("Logo failed to load:", e);
                e.target.style.display = "none";
              }}
            />
          </Box>

          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <Fade in timeout={800}>
              <Box>
                {/* Form Header */}
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: brandColors.primary,
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Welcome Back
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      color: brandColors.secondary,
                      opacity: 0.8,
                    }}
                  >
                    Sign in to your OPS Tracker account
                  </Typography>
                </Box>

                {/* Login Form */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: alpha(brandColors.accent, 0.08),
                        color: brandColors.accent,
                        border: `1px solid ${alpha(brandColors.accent, 0.2)}`,
                        "& .MuiAlert-icon": {
                          color: brandColors.accent,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    {/* Username Field */}
                    <TextField
                      fullWidth
                      label="Username"
                      name="username"
                      autoComplete="username"
                      value={credentials.username}
                      onChange={handleChange}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person
                              sx={{
                                color: brandColors.secondary,
                                fontSize: 20,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: alpha("#ffffff", 0.9),
                          "& fieldset": {
                            borderColor: alpha(brandColors.secondary, 0.2),
                          },
                          "&:hover fieldset": {
                            borderColor: brandColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: brandColors.primary,
                            borderWidth: 2,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: brandColors.secondary,
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: brandColors.primary,
                        },
                      }}
                    />

                    {/* Password Field */}
                    <TextField
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={credentials.password}
                      onChange={handleChange}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock
                              sx={{
                                color: brandColors.secondary,
                                fontSize: 20,
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={togglePasswordVisibility}
                              edge="end"
                              size="small"
                              sx={{
                                color: brandColors.secondary,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    brandColors.secondary,
                                    0.1
                                  ),
                                },
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 4,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: alpha("#ffffff", 0.9),
                          "& fieldset": {
                            borderColor: alpha(brandColors.secondary, 0.2),
                          },
                          "&:hover fieldset": {
                            borderColor: brandColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: brandColors.primary,
                            borderWidth: 2,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: brandColors.secondary,
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: brandColors.primary,
                        },
                      }}
                    />

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      size="large"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
                        boxShadow: `0 4px 15px ${alpha(
                          brandColors.primary,
                          0.3
                        )}`,
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: `0 8px 25px ${alpha(
                            brandColors.primary,
                            0.4
                          )}`,
                          background: `linear-gradient(135deg, ${brandColors.secondary} 0%, ${brandColors.primary} 100%)`,
                        },
                        "&:disabled": {
                          background: "#e0e0e0",
                          color: "#9e9e9e",
                          boxShadow: "none",
                          transform: "none",
                        },
                        transition: "all 0.3s ease",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                          transition: "left 0.5s ease",
                        },
                        "&:hover::before": {
                          left: "100%",
                        },
                      }}
                    >
                      {loading ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress size={20} color="inherit" />
                          Signing In...
                        </Box>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </Box>
                </Paper>

                {/* Footer */}
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 4,
                    pt: 3,
                    borderTop: `1px solid ${alpha(brandColors.secondary, 0.1)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha(brandColors.secondary, 0.7),
                      fontSize: "0.875rem",
                    }}
                  >
                    © 2025 OPS Tracker. All rights reserved.
                  </Typography>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
